import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { ConversationErrorCode } from "@/lib/charles/usePulseConversation";

const COPY: Record<
  ConversationErrorCode,
  { title: string; body: string; cta?: { label: string; href: string } }
> = {
  unauthorized: {
    title: "You're signed out",
    body: "Sign back in to start a private conversation. Nothing you say is shared with your organization.",
    cta: { label: "Sign in", href: "/auth/signin" },
  },
  not_pulse_employee: {
    title: "We can't find your invite yet",
    body: "Your workspace hasn't added you to the pulse program. Check your inbox for an invite, or come back later.",
    cta: { label: "Try again", href: "/app/employee" },
  },
  no_active_membership: {
    title: "Your access is paused",
    body: "Your membership for this workspace isn't active right now. If you think that's a mistake, try again in a bit.",
    cta: { label: "Back to today", href: "/app/employee" },
  },
  config_error: {
    title: "Charles isn't configured here",
    body: "We're missing a setting needed to start the conversation. This is on us — please try again later.",
  },
  network_error: {
    title: "Couldn't reach Charles",
    body: "Your connection dropped before we could start. Check your network and try again.",
  },
  server_error: {
    title: "Something went wrong on our side",
    body: "We couldn't start the conversation. Please try again in a moment.",
  },
  daily_limit: {
    title: "Let's pick this up tomorrow",
    body:
      "You and Charles have already had a lot of conversations today — more than most people need in a week. " +
      "To keep things healthy (for you and for him), we cap it at 100 a day. " +
      "Nothing's lost — your history and pulses are all safe, and Charles will be right here in the morning.",
    cta: { label: "Back to today", href: "/app/employee" },
  },
  mic_denied: {
    title: "Mic isn't available",
    body: "Switched to text for this session. Audio stays off until you allow mic access.",
  },
};

export function CharlesErrorState({ code }: { code: ConversationErrorCode }) {
  const c = COPY[code];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading">{c.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{c.body}</p>
        {c.cta && (
          <Button asChild variant="outline">
            <a href={c.cta.href}>{c.cta.label}</a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function getCharlesErrorCopy(code: ConversationErrorCode) {
  return COPY[code];
}
